import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ParentSettlements.css';

const ParentSettlements = () => {
  const navigate = useNavigate();
  const [settlementData, setSettlementData] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState('');
  const [settlementHistory, setSettlementHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isAlreadySettled, setIsAlreadySettled] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/parent/login');
      return;
    }

    // 현재 주로 초기화
    const today = new Date();
    const dayOfWeek = today.getDay();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - dayOfWeek);
    setSelectedWeek(startDate.toISOString().split('T')[0]);

    loadSettlementPreview();
    loadSettlementHistory();
  }, [navigate]);

  useEffect(() => {
    if (selectedWeek) {
      loadSettlementPreview();
    }
  }, [selectedWeek]);

  const loadSettlementPreview = async () => {
    if (!selectedWeek) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v0/settlements/preview?week=${selectedWeek}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSettlementData(data);

        // 이미 정산된 주인지 확인
        const isSettled = settlementHistory.some(history =>
          history.week === selectedWeek
        );
        setIsAlreadySettled(isSettled);
      } else {
        throw new Error('정산 미리보기 로딩 실패');
      }
    } catch (error) {
      console.error('정산 미리보기 오류:', error);
      setMessage('정산 데이터를 불러올 수 없습니다.');
      // 오류 발생 시 정산 상태 초기화
      setIsAlreadySettled(false);
    } finally {
      setLoading(false);
    }
  };

  const loadSettlementHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v0/settlements/history', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSettlementHistory(data.settlements || []);
      }
    } catch (error) {
      console.error('정산 이력 오류:', error);
    }
  };

  const handleSettlementConfirm = async () => {
    if (!settlementData || settlementData.children.length === 0) {
      setMessage('정산할 데이터가 없습니다.');
      return;
    }

    if (isAlreadySettled) {
      setMessage('❌ 이미 정산이 완료된 주입니다.');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const childrenData = settlementData.children.map(child => ({
        childId: child.child.id,
        totalMissions: child.stats.totalMissions,
        completedMissions: child.stats.completedMissions,
        completionRate: child.stats.completionRate,
        baseAllowance: child.calculation.baseAllowance,
        finalAmount: child.calculation.finalAmount,
        bonusAmount: child.calculation.bonusAmount,
        penaltyAmount: child.calculation.penaltyAmount
      }));

      const response = await fetch('/api/v0/settlements/confirm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          week: selectedWeek,
          children: childrenData
        })
      });

      if (response.ok) {
        const result = await response.json();
        setMessage(`✅ ${result.message}`);

        // 즉시 정산 완료 상태로 설정
        setIsAlreadySettled(true);

        await loadSettlementHistory(); // 이력 새로고침

        // 자동으로 다음 주로 이동
        const nextWeek = new Date(selectedWeek);
        nextWeek.setDate(nextWeek.getDate() + 7);
        const nextWeekString = nextWeek.toISOString().split('T')[0];

        // 2초 후 다음 주로 자동 이동
        setTimeout(() => {
          setSelectedWeek(nextWeekString);
          setMessage(`📅 다음 주(${nextWeekString})로 이동했습니다.`);
          setTimeout(() => setMessage(''), 3000);
        }, 2000);
      } else {
        const error = await response.json();
        throw new Error(error.error || '정산 확정 실패');
      }
    } catch (error) {
      console.error('정산 확정 오류:', error);
      setMessage(`❌ ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
      weekday: 'short'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  if (loading && !settlementData) {
    return <div className="loading">로딩 중...</div>;
  }

  return (
    <div className="parent-settlements">
      <div className="settlements-header">
        <button className="back-btn" onClick={() => navigate('/parent/dashboard')}>
          ← 뒤로가기
        </button>
        <h1>주간 정산</h1>
      </div>

      {message && (
        <div className={`message ${message.includes('❌') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      {/* 주간 선택 */}
      <div className="week-selector">
        <label htmlFor="week-select">정산 주간 선택:</label>
        <input
          type="date"
          id="week-select"
          value={selectedWeek}
          onChange={(e) => setSelectedWeek(e.target.value)}
        />
        <span className="week-info">
          {selectedWeek && `${formatDate(selectedWeek)} ~ ${formatDate(new Date(new Date(selectedWeek).getTime() + 6 * 24 * 60 * 60 * 1000))}`}
        </span>
      </div>

      {/* 정산 미리보기 */}
      {settlementData && (
        <div className="settlement-preview">
          <h2>🧮 정산 미리보기</h2>

          <div className="settlement-summary">
            <div className="summary-item">
              <span>총 지급 예정액:</span>
              <strong>{formatCurrency(settlementData.summary?.totalFamilyAmount || 0)}원</strong>
            </div>
            <div className="summary-item">
              <span>평균 완료율:</span>
              <strong>{settlementData.summary?.averageCompletionRate || 0}%</strong>
            </div>
          </div>

          <div className="children-settlements">
            {settlementData.children.map((childData) => (
              <div key={childData.child.id} className="child-settlement-card">
                <div className="child-header">
                  <h3>{childData.child.name}</h3>
                  <div className="completion-badge">
                    {childData.stats.completionRate}% 완료
                  </div>
                </div>

                <div className="settlement-details">
                  <div className="detail-row">
                    <span>총 미션:</span>
                    <span>{childData.stats.totalMissions}개</span>
                  </div>
                  <div className="detail-row">
                    <span>완료 미션:</span>
                    <span>{childData.stats.completedMissions}개</span>
                  </div>
                  <div className="detail-row">
                    <span>가중 완료율:</span>
                    <span>{childData.stats.weightedCompletionRate}%</span>
                  </div>
                  <div className="detail-row">
                    <span>기본 용돈:</span>
                    <span>{formatCurrency(childData.calculation.baseAllowance)}원</span>
                  </div>
                  {childData.calculation.bonusAmount > 0 && (
                    <div className="detail-row bonus">
                      <span>보너스:</span>
                      <span>+{formatCurrency(childData.calculation.bonusAmount)}원</span>
                    </div>
                  )}
                  {childData.calculation.penaltyAmount > 0 && (
                    <div className="detail-row penalty">
                      <span>벌점:</span>
                      <span>-{formatCurrency(childData.calculation.penaltyAmount)}원</span>
                    </div>
                  )}
                  <div className="detail-row final">
                    <span>최종 지급액:</span>
                    <strong>{formatCurrency(childData.calculation.finalAmount)}원</strong>
                  </div>
                </div>

                {!childData.stats.allRequiredCompleted && (
                  <div className="required-warning">
                    ⚠️ 필수 미션 미완료로 인해 제한이 적용될 수 있습니다.
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="settlement-actions">
            <button
              className="btn-confirm"
              onClick={handleSettlementConfirm}
              disabled={loading || settlementData.children.length === 0 || isAlreadySettled}
            >
              {loading ? '정산 중...' :
               isAlreadySettled ? '✅ 정산 완료됨' :
               '정산 확정하기'}
            </button>
            {isAlreadySettled && (
              <div className="already-settled-notice">
                ℹ️ 이 주는 이미 정산이 완료되었습니다. 정산 이력에서 확인할 수 있습니다.
              </div>
            )}
          </div>
        </div>
      )}

      {/* 정산 이력 */}
      <div className="settlement-history">
        <h2>📋 정산 이력</h2>
        {settlementHistory.length === 0 ? (
          <div className="no-history">아직 정산 이력이 없습니다.</div>
        ) : (
          <div className="history-list">
            {settlementHistory.map((settlement, index) => (
              <div key={settlement.week} className="history-item">
                <div className="history-header">
                  <span className="week-period">
                    {formatDate(settlement.week)} ~ {formatDate(settlement.weekEndDate)}
                  </span>
                  <span className="total-amount">
                    {formatCurrency(settlement.totalAmount)}원
                  </span>
                </div>
                <div className="children-list">
                  {settlement.children.map((child) => (
                    <div key={child.id} className="child-summary">
                      <span>{child.name}</span>
                      <span>{child.completionRate}% ({formatCurrency(child.finalAmount)}원)</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentSettlements;